// sales-page/src/pages/SalesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OTPModal from '../components/OTPModal';
import salesApi from '../core/salesApi';
import { getVisitorFingerprint, getDeviceInfo, getGeoData } from '../utils/fingerprint';
import salesLifetimeCss from './SalesLifetime.css?raw';
import salesLifetimeMarkup from './SalesLifetime.html?raw';
import { legalContent } from './legalContent';

// Public, one-time-payment lifetime offer -- no plan tiers, no promoter gate.
const PLAN = {
  id: 'trafiktrak_lifetime',
  name: 'TrafikTrak Lifetime Access',
  price: 58,
  description: 'One-time payment. Lifetime access. No monthly fees, ever.',
  features: [
    'Instant Click Intelligence — every click logged with IP, device, browser, OS, referrer, and full geolocation',
    'Real Bot Filtering — automatic bot flagging and IP blacklist you control',
    'Smart Routing — one link, different destinations by country, device, OS, or time of day',
    'Bio Pages That Actually Work — 100 professional templates + built-in Bio Inbox',
    'Full Analytics — geography, device, traffic source, live time-series',
    'Campaign Multi-Link — pre-tagged short links for every platform in one click',
    'Lead Capture — put any link behind an email gate',
    'Built-In Affiliate Tracking — no extra tool needed',
    'Full Commercial License — own 100% of your data',
    'Lifetime Access — end the monthly subscription cycle forever'
  ]
};

const SalesPage = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const pendingBuyRef = useRef(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [promoterCode, setPromoterCode] = useState('');
  const [visitorFingerprint, setVisitorFingerprint] = useState('');
  const [legalModal, setLegalModal] = useState(null);

  // Page-scoped CSS: injected on mount, removed on unmount so its global
  // `*`/`body` rules never leak onto /checkout, /partner-pitch, etc.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-sales-lifetime', 'true');
    styleEl.textContent = salesLifetimeCss;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    // Optional attribution only -- this page has no promoter/affiliate gate,
    // unlike the old private 3-tier page. A code is captured if present (for
    // commission attribution) but never required to view or buy.
    const code = salesApi.getPromoterCode() || '';
    setPromoterCode(code);

    const existingVerification = salesApi.getEmailVerification();
    if (existingVerification.verified && existingVerification.email) {
      setEmailVerified(true);
      setVerifiedEmail(existingVerification.email);
    }

    const fingerprint = await getVisitorFingerprint();
    setVisitorFingerprint(fingerprint);

    const deviceInfo = getDeviceInfo();
    const geoData = await getGeoData();

    salesApi.storeVisitorData({
      fingerprint,
      promoter_code: code,
      geo_data: geoData
    });

    if (code) {
      try {
        await salesApi.trackClick({
          promoter_code: code,
          visitor_fingerprint: fingerprint,
          ip_address: geoData.ip_address,
          country: geoData.country,
          country_code: geoData.country_code,
          region: geoData.region,
          city: geoData.city,
          timezone: geoData.timezone,
          latitude: geoData.latitude,
          longitude: geoData.longitude,
          device_type: deviceInfo.device_type,
          browser: deviceInfo.browser,
          browser_version: deviceInfo.browser_version,
          operating_system: deviceInfo.operating_system,
          os_version: deviceInfo.os_version,
          user_agent: deviceInfo.user_agent,
          referrer_source: document.referrer || null,
          landing_page: window.location.href
        });
      } catch (error) {
        console.error('Failed to track click (non-fatal):', error);
      }
    }
  };

  const goToCheckout = (email) => {
    navigate('/checkout', {
      state: {
        plan: PLAN,
        email,
        promoterCode,
        visitorFingerprint
      }
    });
  };

  const handleBuyClick = () => {
    if (!emailVerified) {
      pendingBuyRef.current = true;
      setShowOTPModal(true);
      return;
    }
    goToCheckout(verifiedEmail);
  };

  const handleEmailVerified = (email) => {
    setEmailVerified(true);
    setVerifiedEmail(email);
    if (pendingBuyRef.current) {
      pendingBuyRef.current = false;
      goToCheckout(email);
    }
  };

  // Keep a ref to the latest click handler so the DOM-wiring effect below can
  // run once (dep array []) instead of re-binding -- and restarting the
  // countdown timer -- every time emailVerified/verifiedEmail changes.
  const handleBuyClickRef = useRef(handleBuyClick);
  handleBuyClickRef.current = handleBuyClick;

  // Wire the static markup's CTA buttons to the real buy flow, and run the
  // countdown timer (ported from the static export's inline <script>).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const ctaEls = container.querySelectorAll('.doc-cta-button, .sticky-primary-cta');
    const onCtaClick = (e) => {
      e.preventDefault();
      handleBuyClickRef.current();
    };
    ctaEls.forEach((el) => el.addEventListener('click', onCtaClick));

    const legalEls = container.querySelectorAll('[data-legal]');
    const onLegalClick = (e) => {
      e.preventDefault();
      setLegalModal(e.currentTarget.getAttribute('data-legal'));
    };
    legalEls.forEach((el) => el.addEventListener('click', onLegalClick));

    let totalSeconds = 14 * 60 + 59;
    const hourEls = container.querySelectorAll('.timer-digits.hours');
    const minEls = container.querySelectorAll('.timer-digits.minutes');
    const secEls = container.querySelectorAll('.timer-digits.seconds');
    const footerTimer = container.querySelector('#footer-timer');

    const updateTimers = () => {
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const hStr = h < 10 ? `0${h}` : `${h}`;
      const mStr = m < 10 ? `0${m}` : `${m}`;
      const sStr = s < 10 ? `0${s}` : `${s}`;
      hourEls.forEach((el) => { el.textContent = hStr; });
      minEls.forEach((el) => { el.textContent = mStr; });
      secEls.forEach((el) => { el.textContent = sStr; });
      if (footerTimer) footerTimer.textContent = `${mStr}:${sStr}`;
      totalSeconds = totalSeconds > 0 ? totalSeconds - 1 : 15 * 60;
    };
    updateTimers();
    const intervalId = setInterval(updateTimers, 1000);

    return () => {
      ctaEls.forEach((el) => el.removeEventListener('click', onCtaClick));
      legalEls.forEach((el) => el.removeEventListener('click', onLegalClick));
      clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      {/* eslint-disable-next-line react/no-danger */}
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: salesLifetimeMarkup }} />

      <OTPModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onVerified={handleEmailVerified}
        promoterCode={promoterCode}
        visitorFingerprint={visitorFingerprint}
      />

      {/* ── LEGAL/POLICY MODAL ── */}
      {legalModal && legalContent[legalModal] && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setLegalModal(null)}>
          <div
            className="max-w-2xl w-full relative"
            style={{ background: '#FFFFFF', border: '1.5px solid #111111', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: '32px', maxHeight: '80vh', overflowY: 'auto', fontFamily: "'Arial', sans-serif" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLegalModal(null)}
              style={{ position: 'absolute', top: '18px', right: '18px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666666', lineHeight: 1 }}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 style={{ fontSize: '26px', fontWeight: 900, color: '#111111', marginBottom: '18px' }}>
              {legalContent[legalModal].title}
            </h3>
            <div
              style={{ fontSize: '16px', lineHeight: 1.65, color: '#333333' }}
              dangerouslySetInnerHTML={{ __html: legalContent[legalModal].body }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SalesPage;
